import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wallet, ArrowUp, ArrowDown, Receipt, SignOut } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets.emergentagent.com/wingman/52592614-2f6e-41de-a2fc-8bd4fa178175/attachments/a7b256c6c0d7412eaeb7f8471465bdc5_Dise_o sin t_tulo.png";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => new Date(d).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });

export default function ClientWallet() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/wallet/me")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Error al cargar"));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-card rounded-xl p-8 text-center max-w-md">
          <p className="text-amber-400 mb-4">{error}</p>
          <p className="text-sm text-zinc-400 mb-6">Tu cuenta aún no está vinculada a un perfil de cliente. Contactá al administrador.</p>
          <button onClick={logout} className="bg-white/5 border border-white/10 px-4 py-2 rounded-md">
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-turquoise border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen p-6" data-testid="client-wallet-page">
      <header className="max-w-2xl mx-auto flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="CRIYE" className="w-10 h-10" />
          <div>
            <h1 className="font-heading font-bold text-brand-gradient">CRIYE</h1>
            <p className="text-[10px] tracking-wider uppercase text-zinc-500 font-mono">Mi cuenta</p>
          </div>
        </div>
        <button onClick={logout} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5">
          <SignOut size={14} /> Salir
        </button>
      </header>

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="glass-card rounded-xl p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyanBrand/10 via-transparent to-turquoise/10" />
          <div className="relative">
            <Wallet size={32} className={`mx-auto mb-4 ${data.customer.wallet_balance < 0 ? "text-red-400" : "text-turquoise"}`} weight="duotone" />
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-mono">
              {data.customer.wallet_balance < 0 ? "Saldo deudor" : "Saldo disponible"}
            </p>
            <p className={`text-6xl font-mono font-medium tabular-nums mt-3 ${data.customer.wallet_balance < 0 ? "text-red-400" : "text-white"}`}>
              {data.customer.wallet_balance < 0 ? "-" : ""}$ {fmt(Math.abs(data.customer.wallet_balance))}
            </p>
            <p className="text-sm text-zinc-400 mt-3">{data.customer.name}</p>
            {data.customer.wallet_balance < 0 && (
              <p className="text-xs text-amber-400 mt-2 font-mono">⚠ Cuenta corriente — saldo pendiente de pago</p>
            )}
          </div>
        </div>

        <section className="glass-card rounded-xl">
          <div className="p-5 border-b border-white/5">
            <h2 className="font-heading font-semibold flex items-center gap-2">
              <Receipt size={18} /> Mis movimientos
            </h2>
          </div>
          {data.transactions.length === 0 ? (
            <p className="p-12 text-center text-zinc-500 text-sm">Sin movimientos aún</p>
          ) : (
            <div>
              {data.transactions.map((t) => (
                <div key={t.txn_id} className="flex items-center justify-between p-4 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.type === "debit" ? "bg-red-500/10 text-red-400" : "bg-turquoise/10 text-turquoise"}`}>
                      {t.type === "debit" ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                    </div>
                    <div>
                      <p className="text-sm">{t.description}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">{fmtDate(t.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-medium ${t.type === "debit" ? "text-red-400" : "text-turquoise"}`}>
                      {t.type === "debit" ? "-" : "+"} $ {fmt(t.amount)}
                    </p>
                    <p className="text-[10px] text-zinc-600 font-mono">$ {fmt(t.balance_after)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card rounded-xl">
          <div className="p-5 border-b border-white/5">
            <h2 className="font-heading font-semibold flex items-center gap-2">
              <Receipt size={18} /> Mis compras
            </h2>
          </div>
          {data.sales.length === 0 ? (
            <p className="p-12 text-center text-zinc-500 text-sm">Aún no realizaste compras</p>
          ) : (
            <div>
              {data.sales.map((s) => (
                <div key={s.sale_id} className="p-4 border-b border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-zinc-500 font-mono">{fmtDate(s.created_at)}</p>
                    <p className="font-mono text-turquoise">$ {fmt(s.total)}</p>
                  </div>
                  <p className="text-sm text-zinc-400">
                    {s.items.length} producto(s) · {s.items.map(i => i.name).slice(0, 2).join(", ")}{s.items.length > 2 ? "..." : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
